import React, { useEffect, useState } from 'react';
import { Eyebrow } from '../../design-system/concept/eyebrow';
import { ActionButton } from '../../design-system/concept/action-button';
import { newReportId, reportCrash, sendErrorReport } from '../../lib/errorReport';

/**
 * What a crash shows instead of a blank page (SF-92).
 *
 * The reader is as likely a client on their own phone as a coach, so it says
 * what to try in plain words, gives a reference they can pass on, and lets
 * them say what they were doing. The crash itself has already been sent.
 */
export const ErrorScreen: React.FC<{ reportId: string }> = ({ reportId }) => {
  const [note, setNote] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');

  const send = async () => {
    setState('sending');
    const ok = await sendErrorReport({ kind: 'report', id: reportId, note: note.trim() || 'No details given' });
    setState(ok ? 'sent' : 'failed');
  };

  return (
    <div
      data-testid="error-screen"
      className="flex min-h-[100dvh] items-center justify-center bg-shell p-4 font-sans text-foreground"
    >
      <div className="w-full max-w-sm rounded-lg border border-border bg-card p-8">
        <Eyebrow>Sizewall</Eyebrow>
        <h1 className="mt-2 text-xl font-semibold tracking-tight">Something went wrong</h1>
        <p className="mt-1 text-sm text-foreground-soft">
          The page stopped working. Reloading usually fixes it.
        </p>
        <ActionButton variant="primary" className="mt-5 w-full" onClick={() => window.location.reload()} testId="error-reload">
          Reload
        </ActionButton>

        <p className="mt-6 text-sm font-medium">If it happens again</p>
        <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-foreground-soft">
          <li>Open the link in Safari or Chrome, not inside another app such as Mail or Messenger.</li>
          <li>Check that the phone's software is up to date.</li>
        </ul>

        <div className="mt-6 border-t border-divider pt-5">
          {state === 'sent' ? (
            <p data-testid="error-report-sent" className="text-sm text-foreground-soft">
              Thanks, it's sent. We'll look into it.
            </p>
          ) : (
            <>
              <label htmlFor="error-note" className="text-sm font-medium">
                Tell us what happened <span className="font-normal text-muted-foreground">(optional)</span>
              </label>
              <textarea
                id="error-note"
                data-testid="error-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={1000}
                rows={3}
                placeholder="What were you doing when it stopped?"
                className="mt-2 w-full rounded-md border border-border bg-background p-2 text-sm"
              />
              <ActionButton
                className="mt-2 w-full"
                onClick={send}
                disabled={state === 'sending'}
                testId="error-send"
              >
                {state === 'sending' ? 'Sending…' : 'Send report'}
              </ActionButton>
              {state === 'failed' && (
                <p className="mt-2 text-[12px] text-muted-foreground">
                  It didn't send. Pass the reference below to your coach instead.
                </p>
              )}
            </>
          )}
          <p data-testid="error-reference" className="tabular mt-3 font-mono text-[11px] text-muted-foreground">
            Reference {reportId}
          </p>
        </div>
      </div>
    </div>
  );
};

/** Reports the crash once the screen is up, under the screen's reference. */
const CrashReported: React.FC<{ error: unknown; reportId: string }> = ({ error, reportId }) => {
  useEffect(() => {
    reportCrash(error, reportId, true);
  }, [error, reportId]);
  return <ErrorScreen reportId={reportId} />;
};

/**
 * Around the whole app. Without it, any error while rendering unmounted
 * everything and left an empty page.
 */
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: unknown; reportId: string | null }
> {
  state = { error: null as unknown, reportId: null as string | null };

  static getDerivedStateFromError(error: unknown) {
    return { error, reportId: newReportId() };
  }

  render() {
    if (this.state.reportId) {
      return <CrashReported error={this.state.error} reportId={this.state.reportId} />;
    }
    return this.props.children;
  }
}
